import type { NotificationRequest, ShareAccess, ShareRequest } from 'sr-types/lib/note/v1/graphql';
import type { UserProfile } from '../../auth/api';
import { AllPermissions } from '../../auth/permissions';
import { getContactNameFromObject } from '../../utils/commonUtils';
import { REVOKED_SHARE_ACTION } from '../constants';
import type { ShareAccess as ShareAccessViewModel, ShareAction, ShareSettingsViewModel } from '../types';

const uiActionToAPIActionValueMap = { [AllPermissions.Read]: 'View' as const };
const apiActionToUIActionValueMap = { View: AllPermissions.Read as const };

function mapShareAccessToShareAccessInput(access: ShareAccessViewModel) {
    // Map UI actions to API actions:
    // - AllPermissions.Read  -> 'View' (backend expects 'View' instead of 'Read')
    // - REVOKED_SHARE_ACTION -> revoked: true
    const apiAction = uiActionToAPIActionValueMap[access.action] ?? access.action;

    const payload = {
        actor: access.actor,
        action: apiAction,
        ...(access.action === REVOKED_SHARE_ACTION ? { revoked: true } : {})
    };

    return payload;
}

function mapShareAccessToViewModel(shareAccess: ShareAccess, entityCreatedBy?: string): ShareAccessViewModel {
    const shareAccessViewModel: ShareAccessViewModel = {
        actor: shareAccess.actor,

        /**
         * @todo: Add validation
         */
        action: apiActionToUIActionValueMap[shareAccess.action] ?? (shareAccess.action as ShareAction),
        isOwner: shareAccess.actor.id === entityCreatedBy
    };

    return shareAccessViewModel;
}

export function shareSettingsToShareSettingsInput(
    shareSettings: ShareSettingsViewModel,
    userProfile: UserProfile
): Omit<ShareRequest, 'id'> {
    const restricted = shareSettings.accessLevel === 'restricted';
    const sharedWithAccounts: ShareRequest['sharedWithAccounts'] = (shareSettings.sharedWith ?? []).map(
        mapShareAccessToShareAccessInput
    );
    const sharedWithDepartments: ShareRequest['sharedWithDepartments'] = (
        shareSettings.sharedWithDepartments ?? []
    ).map(mapShareAccessToShareAccessInput);

    if (restricted) {
        const hasOwnerEntry = (shareSettings.sharedWith ?? []).some((shareAccess) => shareAccess.isOwner);

        const existingCurrentUserIdx = sharedWithAccounts.findIndex((acc) => acc.actor.id === userProfile.accountId);

        if (!hasOwnerEntry && existingCurrentUserIdx < 0) {
            sharedWithAccounts.push({
                actor: {
                    id: userProfile.accountId,
                    name: userProfile.name ? getContactNameFromObject(userProfile.name) : userProfile.username,
                    email: userProfile.username
                },
                action: AllPermissions.Manage
            });
        }
    }

    return {
        restricted,
        sharedWithAccounts,
        sharedWithDepartments,
        notify: shareSettings.notify ?? false
    };
}

export function shareSettingsToNotificationInput(
    shareSettings: ShareSettingsViewModel,
    sharedWithAccounts: NonNullable<ShareRequest['sharedWithAccounts']>,
    existingSharedWithAccountIds: string[] = []
): NotificationRequest {
    const notify = shareSettings.notify ?? false;
    if (!notify) {
        return { notify: false };
    }
    const existingAccountIdSet = new Set(existingSharedWithAccountIds);

    const ownerAccountIds = new Set(
        (shareSettings.sharedWith ?? [])
            .filter((shareAccess) => shareAccess.isOwner)
            .map((shareAccess) => shareAccess.actor.id)
    );

    const selectedNotifyContacts = shareSettings.notifyContacts ?? [];
    const notifyActorCandidates =
        selectedNotifyContacts.length > 0
            ? selectedNotifyContacts
            : sharedWithAccounts.map((shareAccess) => shareAccess.actor);

    const dedupedAccountIds = new Set<string>();
    const notifyAccounts = notifyActorCandidates
        .filter((actor) => {
            if (!actor.id) {
                return false;
            }

            if (
                ownerAccountIds.has(actor.id) ||
                existingAccountIdSet.has(actor.id) ||
                dedupedAccountIds.has(actor.id)
            ) {
                return false;
            }

            dedupedAccountIds.add(actor.id);
            return true;
        })
        .map((actor) => ({
            id: actor.id,
            name: actor.name,
            email: actor.email
        }));

    const notificationInput: NotificationRequest = {
        notify,
        notifyAccounts,
        customMessage: shareSettings.customMessage
    };
    return notificationInput;
}

export function shareSettingsDataToShareSettingsViewModel(shareSettingsData: {
    restricted: boolean;
    sharedWithAccounts?: ShareAccess[];
    sharedWithDepartments?: ShareAccess[];
    entityCreatedBy?: string;
    entityCreatedByName?: string;
}): ShareSettingsViewModel {
    const accessLevel = shareSettingsData.restricted ? 'restricted' : 'open';

    const sharedWith = (shareSettingsData.sharedWithAccounts ?? []).map((shareAccess) =>
        mapShareAccessToViewModel(shareAccess, shareSettingsData.entityCreatedBy)
    );

    const sharedWithDepartments = (shareSettingsData.sharedWithDepartments ?? []).map((shareAccess) =>
        mapShareAccessToViewModel(shareAccess)
    );

    const shareSettingsViewModel: ShareSettingsViewModel = {
        accessLevel,
        sharedWith,
        sharedWithDepartments,
        notify: true,
        notifyContacts: [],
        entityCreatedBy: shareSettingsData.entityCreatedBy,
        entityCreatedByName: shareSettingsData.entityCreatedByName
    };

    return shareSettingsViewModel;
}
